// The banner layer (§11, §14.3): countdown stamps "3, 2, 1, BONK!" on the beats at 1.6× with a
// shake (BONK! adds a 15 % white flash unless Reduced Flashing), the round intro card (mode, map,
// one-line rules, GOLDEN ROUND), full-width sweeps (SUDDEN DEATH!, MATCH POINT, REVENGE!, BOUNTY!),
// center stamps (ROUND OVER, "1st: Grumbo!", DRAW!, YOU'RE A GHOST!). GAME!, DOUBLE BONK!, the
// MATCH POINT letterbox and the Sudden Death red pulse are the Renderer's (TextPops, FinalPass).
import type { MapId, ModeId } from '@shared/ids';
import { flash } from '../anim';
import { icon, MODE_ICONS } from '../icons';
import { MODE_BLURBS, MODE_NAMES, mapName } from '../labels';
import { el, replayClass, setHidden, setText, toggleClass } from '../ui';

export type SweepKind = 'danger' | 'gold' | 'revenge' | 'ink' | 'mint';
export type StampKind = 'yellow' | 'gold' | 'pink' | 'white' | 'ghost';

const COUNTDOWN_MS = 900;
const SWEEP_MS = 1500;
const STAMP_MS = 1800;
const INTRO_MS = 2800;

export interface Banners {
  readonly el: HTMLElement;
  countdown(n: number): void;
  intro(mode: ModeId, map: MapId, golden: boolean, extra: string | null): void;
  sweep(text: string, kind: SweepKind, sub?: string): void;
  /** Ends a running sweep now (GAME! owns the screen once the match is decided). */
  endSweep(): void;
  /** `low`: under the screen's middle (the match's last round, where the Renderer's GAME! stamp owns the top). */
  stamp(content: string | Node[], kind: StampKind, sub?: string, ms?: number, low?: boolean): void;
  reset(): void;
  update(nowMs: number): void;
}

export function createBanners(): Banners {
  const count = el('div', { class: 'bn-count', attrs: { 'aria-live': 'assertive' } });
  const introIcon = el('span', { class: 'bi-icon' });
  const introMode = el('div', { class: 'bi-mode' });
  const introMap = el('div', { class: 'bi-map' });
  const introBlurb = el('div', { class: 'bi-blurb' });
  const introGold = el('div', { class: 'bi-gold' }, icon('golden'), el('span', { text: 'GOLDEN ROUND • DOUBLE POINTS' }));
  const introExtra = el('div', { class: 'bi-extra' });
  const introCard = el('div', { class: 'bn-intro' }, introIcon, el('div', { class: 'bi-text' }, introGold, introMode, introMap, introBlurb, introExtra));
  const sweepText = el('div', { class: 'bs-text' });
  const sweepSub = el('div', { class: 'bs-sub' });
  const sweep = el('div', { class: 'bn-sweep' }, el('div', { class: 'bs-band' }, sweepText, sweepSub));
  const stampBody = el('div', { class: 'st-body' });
  const stampSub = el('div', { class: 'st-sub' });
  const stamp = el('div', { class: 'bn-stamp', attrs: { 'aria-live': 'assertive' } }, stampBody, stampSub);
  const root = el('div', { class: 'banners' }, introCard, sweep, stamp, count);
  for (const n of [count, introCard, sweep, stamp]) n.hidden = true;

  let countUntil = 0;
  let introUntil = 0;
  let sweepUntil = 0;
  let stampUntil = 0;

  return {
    el: root,
    countdown(n) {
      const text = n <= 0 ? 'BONK!' : String(n);
      setText(count, text);
      toggleClass(count, 'bonk', n <= 0);
      setHidden(count, false);
      replayClass(count, 'go');
      countUntil = performance.now() + COUNTDOWN_MS;
      if (n <= 0) {
        flash(root, 0.15, 60);
        introUntil = Math.min(introUntil, performance.now() + 150);
      }
    },
    intro(mode, map, golden, extra) {
      introIcon.replaceChildren(icon(golden ? 'golden' : MODE_ICONS[mode]));
      setText(introMode, MODE_NAMES[mode].toUpperCase());
      setText(introMap, `on ${mapName(map)}`);
      setText(introBlurb, MODE_BLURBS[mode]);
      setHidden(introGold, !golden);
      setText(introExtra, extra ?? '');
      setHidden(introExtra, extra === null);
      toggleClass(introCard, 'golden', golden);
      setHidden(introCard, false);
      replayClass(introCard, 'go');
      introUntil = performance.now() + INTRO_MS;
    },
    sweep(text, kind, sub) {
      setText(sweepText, text);
      setText(sweepSub, sub ?? '');
      setHidden(sweepSub, !sub);
      sweep.dataset.kind = kind;
      setHidden(sweep, false);
      replayClass(sweep, 'go');
      sweepUntil = performance.now() + SWEEP_MS;
    },
    stamp(content, kind, sub, ms = STAMP_MS, low = false) {
      toggleClass(stamp, 'low', low);
      if (typeof content === 'string') setText(stampBody, content);
      else stampBody.replaceChildren(...content);
      setText(stampSub, sub ?? '');
      setHidden(stampSub, !sub);
      stamp.dataset.kind = kind;
      setHidden(stamp, false);
      replayClass(stamp, 'go');
      stampUntil = performance.now() + ms;
    },
    endSweep() {
      if (sweepUntil > 0) sweepUntil = 1;
    },
    reset() {
      for (const n of [count, introCard, sweep, stamp]) n.hidden = true;
      countUntil = introUntil = sweepUntil = stampUntil = 0;
    },
    update(now) {
      if (countUntil > 0 && now > countUntil) {
        countUntil = 0;
        setHidden(count, true);
      }
      if (introUntil > 0 && now > introUntil) {
        introUntil = 0;
        introCard.classList.add('leave');
        setTimeout(() => {
          introCard.classList.remove('leave');
          if (introUntil === 0) setHidden(introCard, true);
        }, 260);
      }
      if (sweepUntil > 0 && now > sweepUntil) {
        sweepUntil = 0;
        setHidden(sweep, true);
      }
      if (stampUntil > 0 && now > stampUntil) {
        stampUntil = 0;
        stamp.classList.add('leave');
        setTimeout(() => {
          stamp.classList.remove('leave');
          if (stampUntil === 0) setHidden(stamp, true);
        }, 220);
      }
    },
  };
}
